import React from 'react';
import { Video, Plus, Play, ExternalLink, Check, RefreshCw } from 'lucide-react';

const RecordingsSection = ({
  liveSessions,
  existingRecordings,
  newRecording,
  setNewRecording,
  showAddRecordingModal,
  setShowAddRecordingModal,
  fetchLiveSessions,
  fetchExistingRecordings
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Session Recordings</h1>
          <p className="text-gray-500">Add Zoom recordings from past live sessions</p>
        </div>
        <button 
          onClick={() => {
            fetchLiveSessions();
            fetchExistingRecordings();
            setShowAddRecordingModal(true);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          data-testid="add-recording-btn"
        >
          <Plus className="w-5 h-5" />
          Add Recording
        </button>
      </div>

      {/* Instructions Card */}
      <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl p-6 border border-purple-100">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Video className="w-5 h-5 text-purple-600" />
          How to Add Session Recordings
        </h3>
        <div className="space-y-4 text-sm">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="font-semibold text-green-800 mb-2">✅ Recommended: YouTube or Vimeo (Plays In-App)</p>
            <ol className="list-decimal list-inside space-y-1 text-green-700">
              <li>Upload your recording to YouTube (unlisted) or Vimeo</li>
              <li>Copy the video URL</li>
              <li>Click "Add Recording" and paste the link</li>
              <li>Video will play directly in the app!</li>
            </ol>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-semibold text-blue-800 mb-2">ℹ️ Alternative: Zoom Link (Opens New Tab)</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              <li>Go to your Zoom account and find the cloud recording</li>
              <li>Click "Share" and copy the recording URL</li>
              <li>Click "Add Recording" and paste the link</li>
              <li>Note: Zoom links open in a new tab (Zoom doesn't allow embedding)</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Existing Recordings */}
      {existingRecordings.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-green-600" />
            Available Recordings ({existingRecordings.length})
          </h3>
          <div className="space-y-3">
            {existingRecordings.map((rec) => (
              <div key={rec.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                    <Play className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{rec.title || 'Untitled Recording'}</p>
                    <p className="text-sm text-gray-500">
                      {rec.recorded_at ? new Date(rec.recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date unknown'}
                      {rec.duration_minutes && ` • ${rec.duration_minutes} mins`}
                      {rec.views > 0 && ` • ${rec.views} views`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${rec.is_public ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {rec.is_public ? 'Public' : 'Members Only'}
                  </span>
                  <a 
                    href={rec.recording_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Sessions */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Recent Live Sessions</h3>
        {liveSessions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No live sessions found</p>
            <button
              onClick={fetchLiveSessions}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Refresh Sessions
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {liveSessions.slice(0, 10).map((session) => (
              <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    session.recording_url ? 'bg-green-100' : 'bg-gray-200'
                  }`}>
                    <Video className={`w-5 h-5 ${
                      session.recording_url ? 'text-green-600' : 'text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{session.title}</p>
                    <p className="text-sm text-gray-500">
                      {session.scheduled_time ? new Date(session.scheduled_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : 'No date'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {session.recording_url ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Has Recording
                    </span>
                  ) : (
                    <button
                      onClick={() => {
                        setNewRecording({
                          ...newRecording,
                          session_id: session.id,
                          title: session.title
                        });
                        setShowAddRecordingModal(true);
                      }}
                      className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-medium hover:bg-purple-200"
                    >
                      Add Recording
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingsSection;
